import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { playHarmonicChime } from './sound';
import { enablePushNotifications, getCurrentPushSubscription } from './push';
import { api } from './api';

export interface AlertPayload {
  title: string;
  body: string;
  url?: string;
  tag?: string;
}

export type PermissionStatusType = 'granted' | 'denied' | 'prompt';

/**
 * Checks current notification permission across both Native Android (Capacitor) and Web
 */
export async function getAppNotificationPermission(): Promise<PermissionStatusType> {
  try {
    if (Capacitor.isNativePlatform()) {
      const status = await LocalNotifications.checkPermissions();
      if (status.display === 'granted') return 'granted';
      if (status.display === 'denied') return 'denied';
      return 'prompt';
    }

    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'granted') return 'granted';
      if (Notification.permission === 'denied') return 'denied';
      return 'prompt';
    }
  } catch (err) {
    console.error('Error checking notification permission:', err);
  }
  return 'prompt';
}

/**
 * Requests native notification permissions from the OS and registers backend push
 */
export async function requestAppNotificationPermission(): Promise<{
  success: boolean;
  permission: PermissionStatusType;
  error?: string;
}> {
  try {
    let granted = false;

    if (Capacitor.isNativePlatform()) {
      // 1. Android 13+ native permission prompt
      const localPerm = await LocalNotifications.requestPermissions();
      try {
        await PushNotifications.requestPermissions();
      } catch {}

      granted = localPerm.display === 'granted';
    } else if (typeof window !== 'undefined' && 'Notification' in window) {
      // 2. Standard Web Push permission prompt
      const res = await Notification.requestPermission();
      granted = res === 'granted';
    }

    if (!granted) {
      return {
        success: false,
        permission: 'denied',
        error: 'Notification permission was denied. Please allow notifications in device settings.',
      };
    }

    // Register Web Push subscription with Cloudflare backend if supported
    try {
      await enablePushNotifications();
    } catch {}

    // Dispatch verification chime and test alert
    playHarmonicChime();
    await showAppNotification({
      title: '🔔 CK Pulse Alerts Activated',
      body: 'Notifications are enabled! You will receive instant workstation alerts on this device.',
      url: '/admin/live',
      tag: 'ckpulse-activated',
    });

    return {
      success: true,
      permission: 'granted',
    };
  } catch (err: any) {
    console.error('Error requesting notification permission:', err);
    return {
      success: false,
      permission: 'denied',
      error: err.message || 'Failed to request notification permission.',
    };
  }
}

/**
 * Displays a notification on the device through the best available channel
 * (Capacitor Local Notifications on mobile, Service Worker on PWA, or Web Notification API)
 */
export async function showAppNotification(payload: AlertPayload): Promise<void> {
  const { title, body, url = '/admin/live' } = payload;

  // 1. Play audio chime
  playHarmonicChime();

  // 2. Vibrate mobile device if hardware supports it
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate([250, 100, 250]);
    } catch {}
  }

  // 3. Android Native Capacitor Notification
  if (Capacitor.isNativePlatform()) {
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: Math.floor(Math.random() * 1000000),
            title,
            body,
            extra: { url },
            schedule: { at: new Date(Date.now() + 100) },
            sound: undefined,
          },
        ],
      });
      return;
    } catch (e) {
      console.warn('Capacitor LocalNotifications schedule failed, falling back to Web API:', e);
    }
  }

  // 4. Browser Service Worker Notification (works in background & mobile Chrome)
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      if (reg && reg.showNotification) {
        await reg.showNotification(title, {
          body,
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          data: { url },
          tag: payload.tag || 'ckpulse-' + Date.now(),
          renotify: true,
        } as any);
        return;
      }
    } catch {}
  }

  // 5. Desktop / Window Notification fallback
  if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
    try {
      new Notification(title, {
        body,
        icon: '/icon-192.png',
      });
    } catch {}
  }
}
